/**
 * Generic handler to trace client errors in Heap
 * @param {*} properties custom Heap event properties
 */
export const heapTrackClientError = (properties) => {
  if (!window.heap) return false;
  window.heap.track('NPC-Client-Error', properties);
  return true;
};

/**
 * Track 401 UNAUTHORIZED separately from other client errors in Heap
 * @param {*} properties custom Heap event properties
 */
export const heapTrackUnauthorized = (properties) => {
  if (!window.heap) return false;
  window.heap.track('NPC-Unauthorized', properties);
  return true;
};

/**
 * Track PlaidLink onEvent callback in Heap
 * @param {*} properties refer to PlaidLink.onEvent documentation for possible values
 */
export const heapTrackPlaidEvent = (properties) => {
  if (!window.heap) return false;
  window.heap.track('NPC - Plaid Event', properties);
  return true;
};

/**
 * Track PlaidLink onExit callback in Heap
 * @param {*} properties refer to PlaidLink.onExit documentation for possible values
 */
export const heapTrackPlaidExitedLinkFlow = (properties) => {
  if (!window.heap) return false;
  window.heap.track('NPC - Plaid Exited Link Flow', properties);
  return true;
};

export const heapTrackOfferCodeSubmitted = () => {
  if (!window.heap) return false;
  window.heap.track('NPC - Offer Code Submitted');
  return true;
};

export const heapTrackAnnualIncomeUpdated = () => {
  if (!window.heap) return false;
  window.heap.track('NPC - Annual Income Updated');
  return true;
};

export const heapTrackOptimizelyExperimentEvent = (properties) => {
  if (!window.heap) return false;
  window.heap.track('NPC - Experiment Event', properties);
  return true;
};

export const heapTrackExitPromptDisplayed = (displayed) => {
  if (!window.heap) return false;
  window.heap.track('NPC - Exit Prompt', {
    displayed,
  });
  return true;
};

export const heapTrackElementRendered = (elementName) => {
  if (!window.heap) return false;
  if (elementName) {
    window.heap.track('NPC - Element Rendered', {
      elementName,
    });
    return true;
  }
  return false;
};

// We need to call this w/ UPPER CASE externalUserId
// platform-user responds w/ All Caps externalUserId when we getLoggedInUser or createUser
// so other parts of the Prosper stack call heapIdentify w/ all caps
// and we need to track consistently
export const heapIdentify = (externalUserId) => {
  if (!window.heap) return false;
  window.heap.identify(externalUserId.toUpperCase());
  return true;
};

export const heapUserId = () => {
  if (!window.heap) return null;
  return window.heap.userId;
};

export const heapResetIdentity = () => {
  if (!window.heap) return false;
  window.heap.resetIdentity();
  return true;
};

// properties is a POJO of key value pairs
// ex: heapAddUserProperties({ creditCardApplicationId: `${applicationId}` });
export const heapAddUserProperties = (properties) => {
  if (!window.heap) return false;
  window.heap.addUserProperties(properties);
  return true;
};

export const nrTrackPlaidEvent = (plaidEventProps, applicationId) => {
  if (!window.newrelic) return;
  const {
    eventName,
    errorType,
    errorCode,
    errorMessage,
    exitStatus,
    institutionId,
    institutionName,
    institutionSearchQuery,
    mfaType,
    widgetViewName,
    linkSessionId,
    requestId,
    timestamp,
  } = plaidEventProps;
  // Format the metadata timestamp
  const parsedTimestamp = Date.parse(timestamp);
  // Construct payload with non-null metadata values only
  const payload = {
    externalApplicationId: applicationId,
  };
  if (eventName) payload.eventName = eventName;
  if (parsedTimestamp) payload.timestamp = parsedTimestamp;
  if (errorType) payload.errorType = errorType;
  if (errorCode) payload.errorCode = errorCode;
  if (errorMessage) payload.errorMessage = errorMessage;
  if (exitStatus) payload.exitStatus = exitStatus;
  if (institutionId) payload.institutionId = institutionId;
  if (institutionName) payload.institutionName = institutionName;
  if (institutionSearchQuery)
    payload.institutionSearchQuery = institutionSearchQuery;
  if (mfaType) payload.mfaType = mfaType;
  if (widgetViewName) payload.viewName = widgetViewName;
  if (linkSessionId) payload.linkSessionId = linkSessionId;
  if (requestId) payload.requestId = requestId;
  // Send new PageAction event
  window.newrelic.addPageAction('Plaid Event', payload);
};

export const nrTrackPlaidExit = (plaidExitProps, applicationId) => {
  if (!window.newrelic) return;
  const {
    errorType,
    errorCode,
    errorMessage,
    errorDisplayMessage,
    institutionId,
    institutionName,
    status,
    linkSessionId,
    requestId,
  } = plaidExitProps;
  // Construct payload with non-null metadata values only
  const payload = {
    externalApplicationId: applicationId,
  };
  if (errorType) payload.errorType = errorType;
  if (errorCode) payload.errorCode = errorCode;
  if (errorMessage) payload.errorMessage = errorMessage;
  if (errorDisplayMessage) payload.errorDisplayMessage = errorDisplayMessage;
  if (institutionId) payload.institutionId = institutionId;
  if (institutionName) payload.institutionName = institutionName;
  if (status) payload.status = status;
  if (linkSessionId) payload.linkSessionId = linkSessionId;
  if (requestId) payload.requestId = requestId;
  // Send new PageAction event
  window.newrelic.addPageAction('Plaid Exit', payload);
};

export const nrTrackPlaidSuccess = (plaidSuccessProps, applicationId) => {
  if (!window.newrelic) return;
  const { publicToken, institutionId, institutionName, linkSessionId } =
    plaidSuccessProps;
  // Construct payload with non-null metadata values only
  const payload = {
    externalApplicationId: applicationId,
  };
  if (publicToken) payload.publicToken = publicToken;
  if (institutionId) payload.institutionId = institutionId;
  if (institutionName) payload.institutionName = institutionName;
  if (linkSessionId) payload.linkSessionId = linkSessionId;
  // Send new PageAction event
  window.newrelic.addPageAction('Plaid Success', payload);
};

/**
 * Add userID as a custom attribute to subsequent BrowserInteractions and PageAction events
 * @param {*} userId External User ID
 */
export const nrIdentify = (userId) => {
  if (!window.newrelic) return;
  const externalUserId = userId ? userId.toUpperCase() : '';
  if (externalUserId)
    window.newrelic.setCustomAttribute('externalUserId', externalUserId);
};

/**
 * Add custom attribute to subsequent BrowserInteractions and PageAction events
 * @param {*} propertyName name of property to add to all subsequent events
 * @param {*} propertyValue value of property to add to all subsequent events
 */
export const nrAddPageProperty = (propertyName, propertyValue) => {
  if (!window.newrelic) return;
  if (!propertyName || !propertyValue) return;
  window.newrelic.setCustomAttribute(propertyName, propertyValue);
};

/**
 * Log successful application status transitions to New Relic
 * @param {*} responseData data object in response of processCreditCardApplicationWorkflowEvent
 */
export const nrTrackCreditCardApplicationWorkflowEvent = (responseData) => {
  if (!window.newrelic) return;
  if (!responseData || !responseData.processCreditCardApplicationWorkflowEvent)
    return;
  const { applicationId, applicationStatus, applicationWorkflowEvent } =
    responseData.processCreditCardApplicationWorkflowEvent;
  if (!applicationId || !applicationStatus || !applicationWorkflowEvent) return;
  const payload = {
    externalApplicationId: applicationId,
    applicationStatus,
    applicationWorkflowEvent,
  };
  // Send new PageAction event
  window.newrelic.addPageAction('Application Workflow Event', payload);
};

export const nrTrackOptimizelyExperimentEvent = (
  experimentName,
  experimentGroup,
  experimentEvent
) => {
  if (!window.newrelic) return;
  if (!experimentName || !experimentGroup || !experimentEvent) return;
  const payload = {
    experimentName,
    experimentGroup,
    experimentEvent,
  };
  // Send new PageAction event
  window.newrelic.addPageAction('Experiment Event', payload);
};

// Use this helper to try to ensure we have a stack trace to send.
// (Errors may not have stack traces if they weren't thrown.)
export const withStack = (err) => {
  if (err.stack) {
    return err;
  }
  try {
    throw err;
  } catch (thrownErr) {
    // Note .stack is non-standard property, so there is no
    // guarantee that all browsers will support it.
    thrownErr.stack = thrownErr.stack || '';
    return thrownErr;
  }
};

// Sends an error to New Relic w/ optional extra data
export const logErrorToNR = (err, atts) => {
  if (window.newrelic && window.newrelic.noticeError) {
    if (atts) {
      window.newrelic.noticeError(withStack(err), atts);
    } else {
      window.newrelic.noticeError(withStack(err));
    }
  }
};

// Use this if you want to do several logging operations
// at once, e.g. log to both New Relic and Heap
export const logError = (err, attributes) => {
  try {
    const errWithStack = withStack(err);
    if (err.message !== 'UNAUTHORIZED') {
      logErrorToNR(errWithStack, attributes);
      heapTrackClientError({
        errorName: errWithStack.name,
        errorMessage: errWithStack.message,
        errorStack: errWithStack.stack,
        ...attributes,
      });
    } else {
      heapTrackUnauthorized({
        errorName: errWithStack.name,
        errorMessage: errWithStack.message,
        errorStack: errWithStack.stack,
        ...attributes,
      });
    }
  } catch (e) {} // eslint-disable-line no-empty
};

export const pushToDataLayer = (attributes) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(attributes);
};

export const logExperimentEvent = (
  experimentName,
  experimentGroup,
  experimentEvent
) => {
  try {
    // New Relic
    nrTrackOptimizelyExperimentEvent(
      experimentName,
      experimentGroup,
      experimentEvent
    );
    // Heap
    heapTrackOptimizelyExperimentEvent({
      experimentName,
      experimentGroup,
      experimentEvent,
      timestamp: Date.now(),
    });
  } catch (error) {
    const defaultMessage = `Unable to track event ${experimentEvent} for ${experimentName}`;
    logError(error, {
      errorName: error.name || 'UtilityError',
      errorMessage: error.message || defaultMessage,
      component: 'logging',
      function: 'logExperimentEvent',
    });
  }
};
